/**
 * <s-welcome-popup> — opens a small, non-modal welcome-offer card after a delay.
 *
 * Child markup:
 *   <dialog> — the offer card. Opened with dialog.show() (non-modal) so the page
 *              stays fully usable behind it. Close buttons use command="close".
 *   [data-welcome-apply] — links that apply the code; clicking one also
 *              remembers the visitor so the card doesn't return.
 *
 * Attributes:
 *   data-delay — seconds before the card appears (default 5).
 *
 * Once closed or used, the card stays hidden for 7 days on that browser.
 * In the theme editor it always appears so the merchant can preview it.
 */
const STORAGE_KEY = 'lit-welcome-offer-dismissed';
const SUPPRESS_MS = 7 * 24 * 60 * 60 * 1000;

class SWelcomePopup extends HTMLElement {
  #ac;
  #timer = null;

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    if (!dialog) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const designMode = Boolean(window.Shopify?.designMode);

    if (!designMode && this.#isSuppressed()) return;

    dialog.addEventListener('close', () => {
      if (!designMode) this.#remember();
    }, opts);

    // Non-modal dialogs don't close on Escape natively.
    dialog.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') dialog.close();
    }, opts);

    this.querySelectorAll('[data-welcome-apply]').forEach((link) => {
      link.addEventListener('click', () => this.#remember(), opts);
    });

    const delay = (Number(this.dataset.delay) || 5) * 1000;
    this.#timer = setTimeout(() => {
      if (!dialog.open) dialog.show();
    }, delay);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#timer);
  }

  #isSuppressed() {
    try {
      const stamp = Number(localStorage.getItem(STORAGE_KEY));
      return Boolean(stamp) && Date.now() - stamp < SUPPRESS_MS;
    } catch {
      return false;
    }
  }

  #remember() {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // Storage unavailable (private mode); the card simply may show again next visit.
    }
  }
}

if (!customElements.get('s-welcome-popup')) {
  customElements.define('s-welcome-popup', SWelcomePopup);
}
